import Base from './base'

export default class MyTasks extends Base {
  listMyTasks(params: object = {}) {
    return this.apiClient.get('/my-tasks', params)
  }

  // ── Personal My Tasks sections ─────────────────────────────────────────────
  // No owner id in any path: these are the caller's own, and the server scopes
  // every one of them to the authenticated user.

  createMyTasksSection(data: object) {
    return this.apiClient.post('/my-tasks/sections', data)
  }

  updateMyTasksSection(sectionId: string, data: object) {
    return this.apiClient.put(`/my-tasks/sections/${sectionId}`, data)
  }

  deleteMyTasksSection(sectionId: string) {
    return this.apiClient.delete(`/my-tasks/sections/${sectionId}`)
  }

  reorderMyTasksSections(sectionIds: string[]) {
    return this.apiClient.put('/my-tasks/sections/reorder', { sectionIds })
  }

  moveMyTasksPlacement(data: { taskId: string; sectionId: string | null; position?: number }) {
    return this.apiClient.put('/my-tasks/placement', data)
  }

  listCategories() {
    return this.apiClient.get('/tasks/categories')
  }

  show(id: string) {
    return this.apiClient.get(`/tasks/${id}`)
  }

  unreadCount() {
    return this.apiClient.get('/tasks/unread-count')
  }

  markRead(id: string) {
    return this.apiClient.post(`/tasks/${id}/read`)
  }

  markAllRead(data: object) {
    return this.apiClient.post('/my-tasks/read', data)
  }

  create(data: object) {
    return this.apiClient.post('/tasks', data)
  }

  update(id: string, data: object) {
    return this.apiClient.put(`/tasks/${id}`, data)
  }

  listActivity(taskId: string, params: object = {}) {
    return this.apiClient.get(`/tasks/${taskId}/activity`, params)
  }

  listSubtasks(taskId: string, params: object = {}) {
    return this.apiClient.get(`/tasks/${taskId}/subtasks`, params)
  }

  moveSubtask(taskId: string, data: object) {
    return this.apiClient.put(`/tasks/${taskId}/subtask-placement`, data)
  }

  listComments(taskId: string, params: object = {}) {
    return this.apiClient.get(`/tasks/${taskId}/comments`, params)
  }

  createComment(taskId: string, data: object) {
    return this.apiClient.post(`/tasks/${taskId}/comments`, data)
  }

  deleteComment(taskId: string, commentId: string) {
    return this.apiClient.delete(`/tasks/${taskId}/comments/${commentId}`)
  }

  updateComment(taskId: string, commentId: string, data: object) {
    return this.apiClient.put(`/tasks/${taskId}/comments/${commentId}`, data)
  }

  delete(id: string) {
    return this.apiClient.delete(`/tasks/${id}`)
  }

  bulkDelete(ids: string[]) {
    return this.apiClient.post('/tasks/bulk-delete', { ids })
  }

  merge(id: string, duplicateIds: string[]) {
    return this.apiClient.post(`/tasks/${id}/merge`, { duplicateIds })
  }

  search(params: {
    query: string
    limit?: number
    includeDescription?: boolean
    titleOnly?: boolean
    topLevelOnly?: boolean
  }) {
    return this.apiClient.get('/tasks/search', params)
  }

  uploadInlineFile(taskId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    return this.apiClient.post(`/tasks/${taskId}/inline-files`, formData)
  }

  /**
   * Upload one comment attachment ahead of the comment itself. Returns the
   * metadata the composer stages until Send — a slow upload never blocks typing
   * and a failed one never eats the typed body.
   */
  uploadCommentAttachment(taskId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    return this.apiClient.post(`/tasks/${taskId}/comment-attachments`, formData)
  }

  addCommentReaction(taskId: string, commentId: string, emoji: string) {
    return this.apiClient.post(`/tasks/${taskId}/comments/${commentId}/reactions`, { emoji })
  }

  removeCommentReaction(taskId: string, commentId: string, emoji: string) {
    return this.apiClient.delete(`/tasks/${taskId}/comments/${commentId}/reactions`, { emoji })
  }

  /** Resolves one comment attachment to a data URL; authorization re-checked. */
  getCommentAttachmentLink(attachmentId: string) {
    return this.apiClient.get(`/tasks/comment-attachments/${attachmentId}/link`)
  }

  addCollaborator(taskId: string, userId: string) {
    return this.apiClient.post(`/tasks/${taskId}/collaborators`, { userId })
  }

  removeCollaborator(taskId: string, userId: string) {
    return this.apiClient.delete(`/tasks/${taskId}/collaborators/${userId}`)
  }

  /** Stop the Arc run working this task. Server-scoped to the task's assignee
   *  and collaborators. */
  cancelArcRun(taskId: string) {
    return this.apiClient.post(`/tasks/${taskId}/arc/cancel`)
  }

  // ── Task Recurrence Rules ──────────────────────────────────────────────────

  getRecurrenceRule(id: string, params: object = {}) {
    return this.apiClient.get(`/task-recurrence-rules/${id}`, params)
  }

  createRecurrenceRule(data: object) {
    return this.apiClient.post('/task-recurrence-rules', data)
  }

  updateRecurrenceRule(id: string, data: object) {
    return this.apiClient.put(`/task-recurrence-rules/${id}`, data)
  }

  deleteRecurrenceRule(id: string) {
    return this.apiClient.delete(`/task-recurrence-rules/${id}`)
  }
}
